<template>
  <SfModalPortal
    modal-id="auto-reconcile-modal"
    :should-use-portal="false"
    :scrollable-content="true"
    size="s"
  >
    <template #title>{{ I18n.t('autoreconciliation.modal_header') }}</template>
    <template #body>
      <form class="sf-form sf-form-full sf-below-0">
        <div v-if="error" class="sf-below-3">
          <SfFlash type="error" :message="error" />
        </div>

        <div class="sf-grid sf-grid-fluid">
          <div v-if="showCollectionDropdown" class="sf-row sf-below-3">
            <div class="sf-col">
              <span class="sf-form-label sf-uppercase sf-pad-l-2">{{
                I18n.t('autoreconciliation.collection')
              }}</span>
              <div class="sf-form-select-wrapper sf-full-width">
                <select
                  class="sf-form-select"
                  data-testid="reconciliation-collection-select"
                  @change="handleCollectionChange"
                >
                  <option
                    v-for="(collection_name, collection) in collections"
                    :key="collection"
                    :selected="collectionSelected(collection)"
                    :value="collection"
                  >
                    <SfText :text="collection_name" />
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div
            v-if="!fieldsLoading"
            class="sf-grid sf-grid-gutter-none sf-grid-fluid sf-below-3"
          >
            <div class="sf-row">
              <div class="sf-col">
                <span class="sf-form-label sf-uppercase sf-pad-l-2">{{
                  I18n.t('autoreconciliation.source')
                }}</span>
              </div>
            </div>
            <div class="sf-row sf-row-eq-height sf-row-bar sf-row-bordered">
              <div v-if="showLedgerSource" class="sf-col sf-sm-6">
                <label class="sf-form-check sf-form-check-rounded sf-pad-2"
                  ><input
                    :checked="sourceChecked('ledger')"
                    name="type"
                    type="radio"
                    data-testid="auto-reconcile-file-source-ledger"
                    @click="handleSourceChangeToLedger"
                  />
                  <div class="sf-level sf-level-top sf-form-check-content">
                    <span class="sf-level-item sf-form-check-indicator"></span>
                    <div class="sf-level-item">
                      <div class="sf-title sf-below-1">
                        {{ I18n.t('autoreconciliation.source_ledger') }}
                      </div>
                      <div class="sf-font-xs sf-dim-el">
                        {{ I18n.t('autoreconciliation.source_ledger_note') }}
                      </div>
                    </div>
                  </div></label
                >
              </div>
              <div :class="{ 'sf-sm-6': showLedgerSource }" class="sf-col">
                <div class="sf-form-check sf-form-check-rounded sf-pad-2">
                  <DirectUploadDropzone
                    :accept="accept"
                    :on-file-added="uploadStarted"
                    :on-file-progress="updateUploadProgress"
                    :on-file-uploaded="handleFileUploaded"
                    ><input
                      :checked="sourceChecked('file')"
                      name="type"
                      type="radio"
                      data-testid="auto-reconcile-file-source-excel"
                    />
                    <div
                      ref="fileDropzone"
                      class="sf-level sf-level-top sf-form-check-content"
                    >
                      <span
                        class="sf-level-item sf-form-check-indicator"
                      ></span>
                      <div class="sf-level-item">
                        <div class="sf-title sf-below-1">
                          {{ I18n.t('autoreconciliation.source_file') }}
                        </div>
                        <div class="sf-font-xs sf-dim-el">
                          {{ I18n.t('autoreconciliation.source_file_note') }}
                        </div>
                        <div
                          v-if="
                            hasAnyExpectedValues(
                              reconciliationFieldsByCollection(
                                selectedCollection
                              )
                            )
                          "
                          class="sf-font-xs sf-dim-el"
                        >
                          {{ I18n.t('autoreconciliation.upload_values') }}
                        </div>
                        <div class="sf-font-xs sf-dim-el">
                          <div
                            v-for="reconciliationField in reconciliationFieldsByCollection(
                              selectedCollection
                            )"
                            :key="reconciliationField.identifier"
                          >
                            <div v-if="hasExpectedValues(reconciliationField)">
                              <span class="sf-font-bold"
                                >{{ reconciliationField.name }}:</span
                              ><span>{{
                                expectedValues(reconciliationField)
                              }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div></DirectUploadDropzone
                  >
                  <div class="sf-level sf-level-top sf-form-check-content">
                    <span
                      class="sf-level-item sf-form-check-indicator sf-invisible"
                    ></span
                    ><a
                      :href="downloadAutoreconciliationTemplatePath"
                      class="sf-level-item sf-font-xs"
                      >{{
                        I18n.t('autoreconciliation.download_example_template')
                      }}</a
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!fieldsLoading && fieldsLoaded" class="sf-row">
            <div class="sf-col">
              <table class="sf-table-border sf-full-width">
                <thead>
                  <tr>
                    <th width="50%">
                      {{ I18n.t('autoreconciliation.reconciliation_field') }}
                    </th>
                    <th width="50%">
                      {{ I18n.t('autoreconciliation.source_field') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="reconciliationField in capitalizedReconciliationFieldsByCollection"
                    :key="reconciliationField.identifier"
                  >
                    <td>
                      <b>{{ reconciliationField.capitalizedName }} </b>
                      <i
                        v-if="hasExpectedValues(reconciliationField)"
                        v-tooltip.placementRight.text="
                          infoTextWithTranslation(reconciliationField)
                        "
                        class="fa fa-info-circle sf-pointer sf-c-text-light"
                      ></i>
                    </td>
                    <td>
                      <div :class="inputFieldClasses(reconciliationField)">
                        <select
                          class="sf-form-select"
                          data-testid="reconciliation-field-select"
                          @change="
                            handleReconciliationFieldChange(
                              reconciliationField.identifier
                            )($event)
                          "
                        >
                          <option
                            v-for="sourceField in sourceFields"
                            :key="sourceField.value"
                            :data-reconciliationfield-name="
                              reconciliationField.name
                            "
                            :data-name="sourceField.name"
                            :data-value="sourceField.value"
                            :selected="
                              sourceField.name &&
                              sourceField.name.trim().toLowerCase() ===
                                reconciliationField.name.toLowerCase()
                            "
                            :value="sourceField.value"
                          >
                            {{ sourceField.name }}
                          </option>
                        </select>
                      </div>
                      <div
                        v-if="isAccountCollectionField(reconciliationField)"
                        class="sf-grid sf-full-width"
                      >
                        <div class="sf-row sf-below-1">
                          <label
                            class="
                              sf-form-check
                              sf-form-check-rounded
                              sf-inline-block
                              sf-level
                              sf-level-between
                            "
                          >
                            <input
                              type="radio"
                              :name="
                                accountsRadioBtnName(
                                  reconciliationField.identifier
                                )
                              "
                              value="original"
                              checked="checked"
                              @change="
                                handleReconciliationFieldAccountsChange(
                                  reconciliationField.identifier
                                )($event)
                              "
                            />
                            <span class="sf-form-check-content sf-level">
                              <span class="sf-form-check-indicator"></span>
                              <span
                                :title="
                                  I18n.t(
                                    'autoreconciliation.original_accounts_tooltip'
                                  )
                                "
                                data-toggle="tooltip"
                                data-placement="bottom"
                                >{{
                                  I18n.t('autoreconciliation.original_accounts')
                                }}</span
                              >
                            </span>
                          </label>
                        </div>
                        <div class="sf-row">
                          <label
                            class="
                              sf-form-check
                              sf-form-check-rounded
                              sf-inline-block
                              sf-level
                              sf-level-between
                            "
                          >
                            <input
                              type="radio"
                              :name="
                                accountsRadioBtnName(
                                  reconciliationField.identifier
                                )
                              "
                              value="mapped"
                              @change="
                                handleReconciliationFieldAccountsChange(
                                  reconciliationField.identifier
                                )($event)
                              "
                            />
                            <span class="sf-form-check-content sf-level">
                              <span class="sf-form-check-indicator"></span>
                              <span>{{
                                I18n.t('autoreconciliation.mapped_accounts')
                              }}</span>
                            </span>
                          </label>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else-if="fieldsLoading" class="sf-row">
            <div
              class="sf-col sf-align-c"
              data-testid="auto-reconcile-loading-indicator"
            >
              <div class="sf-spinner sf-inline-block"></div>
            </div>
          </div>
        </div>
      </form>
    </template>
    <template #footer>
      <div
        v-if="!fieldsLoading && fieldsLoaded"
        class="sf-row"
        data-testid="reconciliation-modal-footer"
      >
        <div class="sf-col sf-align-c">
          <SfButtonDs
            v-if="!submitting"
            variant="primary"
            class="sf-level-item"
            :disabled="disableButton"
            @click="replace"
          >
            {{ I18n.t('autoreconciliation.replace') }}
          </SfButtonDs>
          <SfButtonDs
            v-if="!submitting"
            variant="primary"
            class="sf-level-item"
            :disabled="disableButton"
            @click="append"
          >
            {{ I18n.t('autoreconciliation.append') }}
          </SfButtonDs>
          <div v-else class="sf-spinner sf-inline-block"></div>
        </div>
      </div>
    </template>
    <template #messages-bottom>
      <div
        v-if="isLedgerImportSelected"
        v-dompurify-html="I18n.t('autoreconciliation.modal_message')"
        class="sf-modal-message"
      />
    </template>
  </SfModalPortal>
</template>
<script>
import { SfButtonDs } from '@silverfin/ui-components'
import capitalize from 'lodash/capitalize'

import DirectUploadDropzone from '@app/shared/vue/components/direct-upload/direct-upload-dropzone'
import { showLoadingState } from '@app/application/reconciliation/handlers/reconciliation-loading-state'
import { askUnreviewConfirmation } from '@app/shared/services/data-alteration-guard'
import SfFlash from '@app/shared/vue/components/sf-flash.vue'
import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import SfText from '@app/shared/vue/components/sf-text.vue'

import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'AutoReconcileModal',

  components: {
    SfButtonDs,
    SfModalPortal,
    SfFlash,
    SfText,
    DirectUploadDropzone,
  },

  data() {
    return {
      error: '',
      submitting: false,
      channelName: undefined,
    }
  },

  computed: {
    ...mapGetters(['reconciliationFieldsByCollection']),

    ...mapState({
      showLedgerSource: (state) => state.showLedgerSource,
      source: (state) => state.source,
      sourceFields: (state) => state.sourceFields,
      collections: (state) => state.collections,
      selectedCollection: (state) => state.selectedCollection,
      reconciliationFields: (state) => state.reconciliationFields,
      autoreconciliationFields: (state) => state.autoreconciliationFields,
      fieldsLoading: (state) => state.fieldsLoading,
      fieldsLoaded: (state) => state.fieldsLoaded,
      accept: (state) => state.accept,
    }),

    capitalizedReconciliationFieldsByCollection() {
      return this.reconciliationFieldsByCollection(this.selectedCollection).map(
        (field) => ({
          ...field,
          capitalizedName: capitalize(field.name),
        })
      )
    },

    showCollectionDropdown() {
      return Object.keys(this.collections).length > 1
    },

    // Dropzone
    addFileMessage() {
      // we don't have to display anything, click is handled from the 'Upload file' DOM element
      return '<span class="sf-btn"></span>'
    },

    downloadAutoreconciliationTemplatePath() {
      return Silverfin.Routes.download_autoreconciliation_template_reconciliation_texts_path(
        Silverfin.currentFirmId,
        Silverfin.currentCompanyId,
        {
          headers: this.autoreconciliationFields[
            this.selectedCollection
          ].inputs.map((field) => field['name']),
          collection: this.collections[this.selectedCollection],
          format: 'xlsx',
        }
      )
    },

    // we don't want to allow replace or append buttons to be active when
    // the default option "don't use" is only selected in the all available options
    disableButton() {
      return this.capitalizedReconciliationFieldsByCollection.every(
        ({ selected }) => selected === ''
      )
    },

    isLedgerImportSelected() {
      return this.source === 'ledger'
    },
  },

  created() {
    if (this.showLedgerSource) {
      this.updateSource({ source: 'ledger' })
    }
  },

  updated() {
    if (this.fieldsLoaded)
      window.dispatchEvent(new CustomEvent('tooltipsRendered'))
  },

  beforeUnmount() {
    if (this.channelName) this.unsubscribe(this.channelName)
  },

  methods: {
    ...mapActions([
      'updateSelectedCollection',
      'updateReconciliationFieldSelection',
      'updateReconciliationFieldsSelections',
      'updateReconciliationFieldAccounts',
      'updateSource',
      'updateFieldsLoading',
      'submitFields',
    ]),

    unsubscribe(channelName) {
      window.Silverfin.Polling.unsubscribe(
        channelName,
        this.autoreconciliationCompleted
      )
    },

    handleSourceChangeToLedger() {
      this.updateSource({ source: 'ledger' })
    },

    handleCollectionChange(e) {
      this.updateSelectedCollection(e.target.value)

      if (this.sourceChecked('ledger')) {
        this.updateReconciliationFieldsSelections()
      }
    },

    handleReconciliationFieldChange(identifier) {
      return (e) => {
        this.updateReconciliationFieldSelection({
          identifier: identifier,
          selected: e.target.value,
        })
      }
    },

    handleReconciliationFieldAccountsChange(identifier) {
      return (e) => {
        this.updateReconciliationFieldAccounts({
          identifier: identifier,
          accounts: e.target.value,
        })
      }
    },

    async append() {
      const { confirmed } = await askUnreviewConfirmation()
      if (!confirmed) return

      this.doAppend()
    },

    doAppend() {
      this.handleModalButton('append')
    },

    async replace() {
      const { confirmed } = await askUnreviewConfirmation()
      if (!confirmed) return

      this.doReplace()
    },

    doReplace() {
      this.handleModalButton('replace')
    },

    async handleModalButton(method) {
      this.submitting = true

      try {
        const response = await this.submitFields({
          modal: this,
          method: method,
        })

        // autoreconcilation is only scheduled when response.last_id exists, it could be also 0 and it's still valid
        // if last_id is null/undefined, that means nothing has been enqueued and we can just close modal
        if (response.last_id !== null && response.last_id !== undefined) {
          this.channelName = response.channel
          window.Silverfin.Polling.subscribe(
            this.channelName,
            this.handleAutoreconciliationImport,
            response.last_id
          )
        }
      } catch (error) {
        this.handleSubmitError(error)
      }
    },

    handleAutoreconciliationImport(data) {
      if (data.status === 'started') {
        showLoadingState()
        this.handleSubmitCompleted()
      } else if (data.status === 'failed') {
        this.handleSubmitError(data)
      } else if (data.status === 'completed') {
        this.autoreconciliationCompleted()
      }
    },

    autoreconciliationCompleted() {
      window.Turbolinks.visit(window.location.pathname)
    },

    collectionSelected(collection) {
      return collection === this.selectedCollection
    },

    sourceChecked(source) {
      return source === this.source
    },

    hasAnyExpectedValues(collection) {
      return collection.some(this.hasExpectedValues)
    },

    hasExpectedValues(field) {
      return Array.isArray(field.expected_values)
    },

    handleSubmitCompleted() {
      this.submitting = false
      window.modalCitizen.closeModalPortal()
    },

    handleSubmitError(error) {
      this.submitting = false
      this.error = `Something went wrong: ${error?.responseJSON?.error}`
    },

    expectedValues(field) {
      let values

      if (field.expected_values[0] === 'boolean') {
        values = 'true, false'
      } else {
        values = field.expected_values.join(', ')
      }

      return values
    },

    infoTextWithTranslation(field) {
      return `${I18n.t(
        'autoreconciliation.upload_values'
      )} ${this.expectedValues(field)}`
    },

    // Dropzone
    uploadStarted() {
      this.updateFieldsLoading(true)
    },

    updateUploadProgress(progress) {
      return progress
    },

    handleFileUploaded({ blob }) {
      this.updateSource({
        source: 'file',
        optionalParams: { blob_signed_id: blob.signed_id },
      })
    },

    accountsRadioBtnName(identifier) {
      return `accounts[${identifier}]`
    },

    isAccountCollectionField(field) {
      return field.type === 'account_collection'
    },

    inputFieldClasses(field) {
      return `sf-form-select-wrapper sf-full-width ${
        this.isAccountCollectionField(field) ? 'sf-below-1' : ''
      }`
    },
  },
}
</script>
